import { v4 as uuidv4 } from "uuid";
import {
	CommandPayload,
	CommandType,
	LaserPointerStateNotificationPayload,
	CameraFlipRequestPayload,
	CameraFlipResponsePayload
} from "../types/CommandPayload";
import { computed, ref } from "vue";
import Bowser from "bowser";
import { MeetingEvent } from "../types/MeetingEvent";
import { MeetingParticipant } from "../types/MeetingParticipant";
import { IMediaClient } from "../interfaces/IMediaClient";
import { FileDetails } from "../types/FileDetails";
import { uploadToS3, S3UploadError } from "../../../core/s3";

// Ping monitoring constants
const PING_INTERVAL_MS = 1000; // 1 second
const PING_TIMEOUT_MS = 5000; // 5 seconds
const PING_RECOVERY_WINDOW_MS = 5000; // 5 seconds
const PING_RECOVERY_THRESHOLD = 3; // 3 pings within recovery window
const PING_MONITORING_INTERVAL_MS = 1000; // 1 second

// Snapshot processing constants
const SNAPSHOT_QUEUE_INTERVAL_MS = 500; // 500ms
const SNAPSHOT_REQUEST_TIMEOUT_MS = 15000; // 15 seconds

// Camera flip request timeout
const CAMERA_FLIP_REQUEST_TIMEOUT_MS = 10000; // 10 seconds

type SnapshotRequest = CommandPayload & {
	targetUserId: string;
	snapshotId: string;
};

type SnapshotTaken = CommandPayload & {
	snapshotId: string;
};

type SnapshotFailed = CommandPayload & {
	snapshotId: string;
};

type SnapshotUploaded = CommandPayload & {
	snapshotId: string;
	token: string;
};

type SnapshotUploadQueueItem = {
	requestingUserId: string;
	blob: Blob;
	snapshotId: string;
};

type SnapshotRequestQueueItem = {
	snapshotId: string;
	status: "snapshot-requested" | "snapshot-taken" | "snapshot-uploaded" | "snapshot-failed";
	token?: string;
	snapshotUrl?: string;
	thumbnailUrl?: string;
};

abstract class Meeting {
	private _snapshotUploadQueue = ref(new Array<SnapshotUploadQueueItem>());
	private _snapshotRequestQueue = ref(new Array<SnapshotRequestQueueItem>());

	private _isRequestingCameraFlip = ref(false);
	private _cameraFlipResponse = ref(null as CameraFlipResponsePayload | null);

	private _cameraFlipRequestQueue = ref(new Array<CameraFlipRequestPayload>());
	private _cameraFlipRequestVisitorTimeouts = new Map<string, number>();
	private _cameraFlipRequestTimeout: number | null = null;
	private _cameraFlipRequestId: string | null = null;

	private _mediaClient?: IMediaClient;
	public get mediaClient(): IMediaClient | undefined {
		return this._mediaClient;
	}
	public set mediaClient(mediaClient: IMediaClient) {
		this._mediaClient = mediaClient;
	}

	public constructor(mediaClient?: IMediaClient) {
		this._mediaClient = mediaClient;
	}

	public abstract iAmHostOrManager(): boolean;
	public abstract isInitialized(): boolean;

	public async init(token: string): Promise<void> {
		this.processSnapshots();
		this.sendPings();
		this.monitorPings();

		return Promise.resolve();
	}

	public get areSnapshotUploadsQueued() {
		return this._snapshotUploadQueue.value.length > 0;
	}

	public get isTakingSnapshot() {
		return this._takeSnapshotProcessing;
	}

	public get snapshotRequestQueue() {
		return this._snapshotRequestQueue.value;
	}
	public get snapshotUploadQueue() {
		return this._snapshotUploadQueue.value;
	}

	private _isRequestingSnapshot = computed(() => {
		return this._snapshotRequestQueue.value.some((r) => r.status == "snapshot-requested");
	});
	public get isRequestingSnapshot() {
		return this._isRequestingSnapshot.value;
	}

	public get isRequestingCameraFlip() {
		return this._isRequestingCameraFlip.value;
	}

	public get cameraFlipResponse() {
		return this._cameraFlipResponse.value;
	}

	private _anySnapshotFailed = computed(() => {
		return this._snapshotRequestQueue.value.some((r) => r.status == "snapshot-failed");
	});
	public get anySnapshotFailed() {
		return this._anySnapshotFailed.value;
	}

	private _lastSnapshotFailed = computed(() => {
		return this._snapshotRequestQueue.value[this._snapshotRequestQueue.value.length - 1].status == "snapshot-failed";
	});
	public get lastSnapshotFailed() {
		return this._lastSnapshotFailed.value;
	}

	abstract fire(event: string, payload: any): void;
	abstract on(event: string, callback: (payload: any) => void): void;
	abstract off(event: string, callback: (payload: any) => void): void;

	private _rejectedVisitors = ref(new Array<CommandPayload>());
	public get rejectedVisitors() {
		return this._rejectedVisitors.value;
	}

	public get cameraFlipRequest() {
		return this._cameraFlipRequestQueue.value.length > 0 ? this._cameraFlipRequestQueue.value[0] : null;
	}

	public get cameraFlipRequestQueue() {
		return this._cameraFlipRequestQueue.value;
	}

	private _clearCameraFlipRequestTimeout(): void {
		if (this._cameraFlipRequestTimeout !== null) {
			clearTimeout(this._cameraFlipRequestTimeout);
			this._cameraFlipRequestTimeout = null;
		}
	}

	private handleCameraFlipRequest(payload: CameraFlipRequestPayload) {
		// Only handle if this request is for the current user
		if (payload.targetUserId === this.getUserId()) {
			const existingRequest = this._cameraFlipRequestQueue.value.find((r) => r.id === payload.id);
			if (existingRequest) {
				this._cancelRequestTimeout(payload.id);
			} else {
				this._cameraFlipRequestQueue.value.push(payload);
			}

			const timeout = window.setTimeout(() => {
				const index = this._cameraFlipRequestQueue.value.findIndex((r) => r.id === payload.id);
				if (index !== -1) {
					console.log("Camera flip request timed out on visitor side");
					this.notifyCameraFlipTimeout(payload.senderId, payload.id);
					this._cameraFlipRequestQueue.value.splice(index, 1);
				}
				this._cancelRequestTimeout(payload.id);
			}, CAMERA_FLIP_REQUEST_TIMEOUT_MS);
			this._cameraFlipRequestVisitorTimeouts.set(payload.id, timeout);
		}
	}

	private handleCameraFlipResponse(payload: CameraFlipResponsePayload) {
		if (payload.targetUserId === this.getUserId() && payload.requestId === this._cameraFlipRequestId) {
			this._clearCameraFlipRequestTimeout();

			if (payload.commandType === CommandType.CameraFlipAccepted) {
				this._cameraFlipResponse.value = payload;
			} else if (payload.commandType === CommandType.CameraFlipDenied) {
				this._isRequestingCameraFlip.value = false;
				this._cameraFlipResponse.value = payload;
				this._cameraFlipRequestId = null;
			} else if (payload.commandType === CommandType.CameraFlipTimeout) {
				this._isRequestingCameraFlip.value = false;
				this._cameraFlipResponse.value = payload;
				this._cameraFlipRequestId = null;
			} else if (payload.commandType === CommandType.CameraFlipSuccess || payload.commandType === CommandType.CameraFlipFailed) {
				this._isRequestingCameraFlip.value = false;
				this._cameraFlipResponse.value = payload;
				this._cameraFlipRequestId = null;
			}
		} else if (payload.targetUserId === this.getUserId() && payload.requestId !== this._cameraFlipRequestId) {
			console.log("Ignoring stale camera flip response", {
				receivedRequestId: payload.requestId,
				latestRequestId: this._cameraFlipRequestId
			});
		}
	}

	public notifyCameraFlipAccepted(to: string, requestId: string): void {
		const payload: CameraFlipResponsePayload = {
			id: uuidv4(),
			senderId: this.getUserId(),
			senderName: this.getName(),
			timestamp: Date.now(),
			commandType: CommandType.CameraFlipAccepted,
			requestId: requestId,
			targetUserId: to
		};
		this.sendCommand(payload, to).catch((e) => {
			console.error("Error sending camera flip accepted", { to: to, requestId: requestId, error: e });
		});
	}

	public notifyCameraFlipSuccess(to: string, requestId: string): void {
		const payload: CameraFlipResponsePayload = {
			id: uuidv4(),
			senderId: this.getUserId(),
			senderName: this.getName(),
			timestamp: Date.now(),
			commandType: CommandType.CameraFlipSuccess,
			requestId: requestId,
			targetUserId: to
		};
		this.sendCommand(payload, to).catch((e) => {
			console.error("Error sending camera flip success", { to: to, requestId: requestId, error: e });
		});
	}

	public notifyCameraFlipDenied(to: string, requestId: string): void {
		const payload: CameraFlipResponsePayload = {
			id: uuidv4(),
			senderId: this.getUserId(),
			senderName: this.getName(),
			timestamp: Date.now(),
			commandType: CommandType.CameraFlipDenied,
			requestId: requestId,
			targetUserId: to
		};
		this.sendCommand(payload, to).catch((e) => {
			console.error("Error sending camera flip denied", { to: to, requestId: requestId, error: e });
		});
	}

	public notifyCameraFlipFailed(to: string, requestId: string): void {
		const payload: CameraFlipResponsePayload = {
			id: uuidv4(),
			senderId: this.getUserId(),
			senderName: this.getName(),
			timestamp: Date.now(),
			commandType: CommandType.CameraFlipFailed,
			requestId: requestId,
			targetUserId: to
		};
		this.sendCommand(payload, to).catch((e) => {
			console.error("Error sending camera flip failed", { to: to, requestId: requestId, error: e });
		});
	}

	public notifyCameraFlipTimeout(to: string, requestId: string): void {
		const payload: CameraFlipResponsePayload = {
			id: uuidv4(),
			senderId: this.getUserId(),
			senderName: this.getName(),
			timestamp: Date.now(),
			commandType: CommandType.CameraFlipTimeout,
			requestId: requestId,
			targetUserId: to
		};
		this.sendCommand(payload, to).catch((e) => {
			console.error("Error sending camera flip timeout", { to: to, requestId: requestId, error: e });
		});
	}

	// Remove the processed request from the queue
	public clearCameraFlipRequest() {
		if (this._cameraFlipRequestQueue.value.length > 0) {
			const removed = this._cameraFlipRequestQueue.value.shift();
			// Clear timeout for removed request
			if (removed) {
				this._cancelRequestTimeout(removed.id);
			}
		}
	}

	// Clear the response
	public clearCameraFlipResponse() {
		this._cameraFlipResponse.value = null;
	}

	private _cancelRequestTimeout(requestId: string) {
		const timeout = this._cameraFlipRequestVisitorTimeouts.get(requestId);
		if (timeout) {
			clearTimeout(timeout);
			this._cameraFlipRequestVisitorTimeouts.delete(requestId);
		}
	}

	abstract get participants(): MeetingParticipant[];

	private rejectVisitors(payload: CommandPayload) {
		const visitor = this._rejectedVisitors.value.find((v) => v.senderId == payload.senderId);
		if (!visitor) {
			this._rejectedVisitors.value.push(payload);
		}
		console.log("Visitor rejected", payload.senderName);
	}

	public static get isBrowserSupported() {
		const parser = Bowser.getParser(window.navigator.userAgent);
		const result = parser.getResult();
		if (result.browser.name == "Safari" && result.browser.version) {
			if (parseInt(result.browser.version) < 16) {
				return false;
			}
		}
		return true;
	}

	/**
	 * Detects if the current device is a mobile device
	 * @returns An object containing mobile device detection results
	 */
	public static detectMobileDevice(): { isMobileDevice: boolean; isDesktopLikeMobileDevice: boolean } {
		const isSimpleMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
		const iPadPro = !!(navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));

		if (isSimpleMobileDevice || iPadPro) {
			return {
				isMobileDevice: true,
				isDesktopLikeMobileDevice: iPadPro
			};
		} else {
			return {
				isMobileDevice: false,
				isDesktopLikeMobileDevice: false
			};
		}
	}

	public handleCommand(payload: CommandPayload) {
		//console.log(`Command from ${payload.senderName} is ${payload.commandType}`);
		switch (payload.commandType) {
			case "snapshot-requested":
				this.takeSnapshot(payload as SnapshotRequest);
				break;
			case "snapshot-taken":
				this.updateSnapshotStatus((payload as SnapshotTaken).snapshotId, "snapshot-taken", payload).catch((e) => {
					console.log("Error updating snapshot status", e);
				});
				break;
			case "snapshot-uploaded":
				this.updateSnapshotStatus((payload as SnapshotUploaded).snapshotId, "snapshot-uploaded", payload).catch((e) => {
					console.log("Error updating snapshot status", e);
				});
				break;
			case "snapshot-failed":
				this.updateSnapshotStatus((payload as SnapshotFailed).snapshotId, "snapshot-failed", payload).catch((e) => {
					console.log("Error updating snapshot status", e);
				});
				break;
			case "visitor-rejected":
				this.rejectVisitors(payload);
				break;
			case "ping":
				this.recordPing(payload);
				break;
			case CommandType.LaserPointerStateNotification:
				this.handleLaserPointerStateNotification(payload as LaserPointerStateNotificationPayload);
				break;
			case CommandType.SetParticipantOnCanvasMode:
				// handled downstream -- do nothing
				break;
			case CommandType.setUserCameraCount:
				// handled downstream -- do nothing
				break;
			case CommandType.CameraFlipRequested:
				this.handleCameraFlipRequest(payload as CameraFlipRequestPayload);
				break;
			case CommandType.CameraFlipAccepted:
			case CommandType.CameraFlipSuccess:
			case CommandType.CameraFlipDenied:
			case CommandType.CameraFlipFailed:
			case CommandType.CameraFlipTimeout:
				this.handleCameraFlipResponse(payload as CameraFlipResponsePayload);
				break;
			default:
				// Check if this is an optimized laser pointer command (has 'c' field)
				if ((payload as any).c && ["start", "move", "end"].includes((payload as any).c)) {
					//handled by the laser pointer canvas component
					break;
				}
				console.log("Unhandled command type", payload.commandType);
		}
	}

	abstract sendCommand(payload: CommandPayload, to?: string): Promise<void>;
	abstract sendCommandFast(commandType: string, to?: string): void;
	abstract getName(): string;
	abstract getUserId(): string;
	abstract getRoomId(): string;
	abstract getSessionId(): string;
	abstract getFirstActiveNonHostOrManagerParticipantId(): string;

	abstract setSpotlight(userId: string): Promise<void>;
	abstract removeSpotlight(userId: string): Promise<void>;

	private _pings = ref(new Map<string, number>());
	public recordPing(payload: CommandPayload) {
		this._pings.value.set(payload.senderId, Date.now());

		// If user is in not responding list, track their ping count for potential removal
		if (this._notRespondingUsers.value.includes(payload.senderId)) {
			this._trackPingForRemoval(payload.senderId);
		}
	}

	// Track ping counts for users in the not responding list
	private _pingCountsForRemoval = ref(new Map<string, { count: number; firstPingTime: number }>());

	private _trackPingForRemoval(userId: string) {
		const now = Date.now();
		const existing = this._pingCountsForRemoval.value.get(userId);

		if (!existing) {
			// First ping after being marked as not responding
			this._pingCountsForRemoval.value.set(userId, { count: 1, firstPingTime: now });
		} else {
			// Check if we're within the 5-second window
			if (now - existing.firstPingTime <= PING_RECOVERY_WINDOW_MS) {
				existing.count++;

				// If we've received 3 pings within 5 seconds, remove from not responding list
				if (existing.count >= PING_RECOVERY_THRESHOLD) {
					this._removeFromNotRespondingList(userId);
				}
			} else {
				// Reset the counter if outside the 5-second window
				this._pingCountsForRemoval.value.set(userId, { count: 1, firstPingTime: now });
			}
		}
	}

	private _removeFromNotRespondingList(userId: string) {
		const index = this._notRespondingUsers.value.indexOf(userId);
		if (index > -1) {
			this._notRespondingUsers.value.splice(index, 1);
			this._pingCountsForRemoval.value.delete(userId);
			console.log(`User ${userId} is responding again, removed from not responding list`);
			this.fire(MeetingEvent.UserResponding, { userId: userId });
		}
	}

	// Clean up ping tracking when a user leaves the meeting
	public cleanupUserPingTracking(userId: string) {
		this._pings.value.delete(userId);
		this._pingCountsForRemoval.value.delete(userId);
		const index = this._notRespondingUsers.value.indexOf(userId);
		if (index > -1) {
			this._notRespondingUsers.value.splice(index, 1);
		}
	}

	public sendPing(): void {
		if (!this.isInitialized()) {
			return;
		}
		this.sendCommandFast(CommandType.Ping);
	}

	private _snapshotQueueInterval: NodeJS.Timeout | null = null;
	public processSnapshots() {
		this._snapshotQueueInterval = setInterval(() => {
			this.processSnapshotUploadQueue().catch((e) => {
				console.log("Error processing snapshot upload queue", e);
			});
		}, SNAPSHOT_QUEUE_INTERVAL_MS);
	}

	private _sendPingsInterval: NodeJS.Timeout | null = null;
	public sendPings(): void {
		if (this._sendPingsInterval) {
			clearInterval(this._sendPingsInterval);
		}
		this._sendPingsInterval = setInterval(() => {
			this.sendPing();
		}, PING_INTERVAL_MS); // Send ping every second
	}

	private _notRespondingUsers = ref(new Array<string>());
	public get notRespondingUsers() {
		// two lists:
		// list a is the list of users not responding
		// list b is the users that are still "in the meeting" (participants list)
		// we want to return the intersection of these two lists, which is users in the meeting that are not responding
		return this._notRespondingUsers.value.filter((user) => {
			return this.participants.some((participant) => participant.userId === user);
		});
	}

	private _monitorPingsInterval: NodeJS.Timeout | null = null;
	public monitorPings() {
		this._monitorPingsInterval = setInterval(() => {
			const now = Date.now();
			const usersToCheck = Array.from(this._pings.value.entries());

			// Process each user individually to avoid issues with same timestamps
			usersToCheck.forEach(([userId, timestamp]) => {
				if (now - timestamp > PING_TIMEOUT_MS) {
					// 5 seconds since last ping
					console.warn(`User ${userId} is not responding`);
					// delete the user from the pings map
					this._pings.value.delete(userId);

					// Only add to not responding list if not already there AND user is in participants list
					if (
						!this._notRespondingUsers.value.includes(userId) &&
						this.participants.some((participant) => participant.userId === userId)
					) {
						this._notRespondingUsers.value.push(userId);
						// fire an event
						this.fire(MeetingEvent.UserNotResponding, { userId: userId });
						console.log(`Added user ${userId} to not responding list`);
					}
				}
			});
		}, PING_MONITORING_INTERVAL_MS); // Check every second
	}

	public requestSnapshot(from: string): SnapshotRequest | undefined {
		const payload = {
			commandType: "snapshot-requested",
			senderId: this.getUserId(),
			senderName: this.getName(),
			snapshotId: uuidv4(),
			targetUserId: from
		} as SnapshotRequest;
		this.sendCommand(payload, from).catch((e) => {
			console.log("Error sending snapshot request", e);
		}); // ignoring async
		this._snapshotRequestQueue.value.push({
			snapshotId: payload.snapshotId,
			status: "snapshot-requested"
		} as SnapshotRequestQueueItem);

		window.setTimeout(() => {
			const requestQueueItem = this._snapshotRequestQueue.value.find(
				(r) => r.snapshotId == payload.snapshotId && r.status == "snapshot-requested"
			);
			if (requestQueueItem) {
				// remove the request after 5 seconds if it still exists
				console.log("Snapshot request timed out, marking as failed");
				requestQueueItem.status = "snapshot-failed";
			}
		}, SNAPSHOT_REQUEST_TIMEOUT_MS);
		return payload;
	}

	public requestCameraFlip(from: string): CameraFlipRequestPayload | undefined {
		this._clearCameraFlipRequestTimeout();

		const payload: CameraFlipRequestPayload = {
			id: uuidv4(),
			senderId: this.getUserId(),
			senderName: this.getName(),
			timestamp: Date.now(),
			commandType: CommandType.CameraFlipRequested,
			targetUserId: from
		};

		this._cameraFlipRequestId = payload.id;

		this.sendCommand(payload, from).catch((e) => {
			console.log("Error sending camera flip request", e);
			this._clearCameraFlipRequestTimeout();
			this._isRequestingCameraFlip.value = false;
			this._cameraFlipRequestId = null;
		});
		this._isRequestingCameraFlip.value = true;

		this._cameraFlipRequestTimeout = window.setTimeout(() => {
			console.log("Camera flip request timed out on agent side - no response received");
			this._isRequestingCameraFlip.value = false;
			this._cameraFlipRequestTimeout = null;
		}, CAMERA_FLIP_REQUEST_TIMEOUT_MS);

		return payload;
	}

	public notifySnapshotTaken(to: string, snapshotId: string): SnapshotTaken | undefined {
		const payload = {
			commandType: "snapshot-taken",
			senderId: this.getUserId(),
			senderName: this.getName(),
			snapshotId: snapshotId
		} as SnapshotTaken;
		this.sendCommand(payload, to).catch((e) => {
			console.log("Error sending snapshot taken", e);
		}); // ignoring async
		return payload;
	}

	public notifySnapshotFailed(to: string, snapshotId: string): void {
		const payload = {
			commandType: "snapshot-failed",
			senderId: this.getUserId(),
			senderName: this.getName(),
			snapshotId: snapshotId
		} as SnapshotFailed;
		this.sendCommand(payload, to).catch((e) => {
			console.log("Error sending snapshot failed", e);
		}); // ignoring async
	}

	public notifySnapshotUploaded(to: string, snapshotId: string, token: string): SnapshotUploaded | undefined {
		const payload = {
			commandType: "snapshot-uploaded",
			senderId: this.getUserId(),
			senderName: this.getName(),
			token: token,
			snapshotId: snapshotId
		} as SnapshotUploaded;
		this.sendCommand(payload, to).catch((e) => {
			console.log("Error sending snapshot uploaded", e);
		}); // ignoring async
		return payload;
	}

	abstract getSnapshot(): Promise<Blob | null>;

	async destroy(willRebuild: boolean): Promise<void> {
		if (this.canDeactivateLaserPointer()) {
			this.deactivateLaserPointer();
		}

		if (this._sendPingsInterval) {
			clearInterval(this._sendPingsInterval);
			this._sendPingsInterval = null;
		}
		if (this._monitorPingsInterval) {
			clearInterval(this._monitorPingsInterval);
			this._monitorPingsInterval = null;
		}
		if (this._snapshotQueueInterval) {
			clearInterval(this._snapshotQueueInterval);
			this._snapshotQueueInterval = null;
		}

		// Clean up ping tracking maps
		this._pings.value.clear();
		this._pingCountsForRemoval.value.clear();
		this._notRespondingUsers.value.length = 0;

		// Clean up camera flip state
		this._cameraFlipRequestQueue.value.length = 0;
		this._isRequestingCameraFlip.value = false;
		this._cameraFlipResponse.value = null;

		// Clear agent-side timeout
		this._clearCameraFlipRequestTimeout();
		this._cameraFlipRequestId = null;

		// Clear all visitor-side timeouts
		this._cameraFlipRequestVisitorTimeouts.forEach((timeout) => clearTimeout(timeout));
		this._cameraFlipRequestVisitorTimeouts.clear();

		// cleanup any resources, listeners, etc.
		console.log("Meeting destroyed - will rebuild: ", willRebuild);
	}

	private _takeSnapshotQueue = ref(new Array<SnapshotRequest>());
	private _takeSnapshotProcessing = false;
	private _count = 0;
	private takeSnapshot(payload: SnapshotRequest) {
		// can't take multiple snapshots at the same time
		if (this._takeSnapshotProcessing) {
			console.log("Snapshot already in progress, queueing snapshot");
			this._takeSnapshotQueue.value.push(payload);
			return;
		}

		this._count++;
		/*
        this is useful for debugging - causes a timeout every 2nd snapshot
        if(this._count %2 == 0) {
            return
        }*/

		this._takeSnapshotProcessing = true;
		this.getSnapshot()
			.then((blob) => {
				if (!blob) {
					// Snapshot couldn't be taken
					this.notifySnapshotFailed(payload.senderId, payload.snapshotId);
					return;
				}
				this._snapshotUploadQueue.value.push({
					requestingUserId: payload.senderId,
					blob: blob as Blob,
					snapshotId: payload.snapshotId
				});
				this.notifySnapshotTaken(payload.senderId, payload.snapshotId);
			})
			.catch((e) => {
				console.log("Error getting snapshot", e);
				this.notifySnapshotFailed(payload.senderId, payload.snapshotId);
			})
			.finally(() => {
				this._takeSnapshotProcessing = false;
				if (this._takeSnapshotQueue.value.length > 0) {
					const nextSnapshot = this._takeSnapshotQueue.value.shift();
					if (nextSnapshot) {
						// deferring the next snapshot to allow the current one to clean up
						window.setTimeout(() => {
							this.takeSnapshot(nextSnapshot);
						}, 1);
					}
				}
			});
	}

	private _snapshotUploading = false;
	private async processSnapshotUploadQueue() {
		if (this._snapshotUploadQueue.value.length == 0) return;
		if (this._snapshotUploading) return;
		if (!this._mediaClient) {
			console.error("Media client is not set, cannot upload snapshot");
			return;
		}
		this._snapshotUploading = true;

		const queueItem = this._snapshotUploadQueue.value.shift();
		try {
			if (!queueItem) {
				return;
			}

			const snapshotToken = uuidv4();
			const mimeType = queueItem.blob.type;
			const fileType = mimeType.split("/")[0];
			const fileExtension = mimeType.split("/")[1];
			const fileName = `file.${this.getRoomId()}.${snapshotToken}.${fileExtension}`;
			const fileDetails: FileDetails = {
				originalFileName: fileName,
				fileExtension: fileExtension,
				fileType: mimeType
			};
			const presignedUrls = await this._mediaClient.getFilePresignedUrl(this.getRoomId(), [fileDetails], fileType);
			const url = presignedUrls?.[0];

			if (!url) {
				throw new Error(`No presigned URL found for snapshot Id=${queueItem.snapshotId}`);
			}

			await uploadToS3(queueItem.blob as File, url, (result: Response | S3UploadError, file, startTime) => {
				if (!result || !(result instanceof Response) || !result.ok) {
					console.error(`Snapshot: Status=Failed, Id=${queueItem.snapshotId}, ConversationId=${this.getRoomId()}`, file, result);
					this.notifySnapshotFailed(queueItem.requestingUserId, queueItem.snapshotId);
				} else {
					console.log(
						`Snapshot: Status=Uploaded, Id=${queueItem.snapshotId}, ConversationId=${this.getRoomId()}, Token=${snapshotToken}`
					);
					this.notifySnapshotUploaded(queueItem.requestingUserId, queueItem.snapshotId, snapshotToken);
				}
			});
		} catch (e) {
			console.error("Error uploading snapshot", e);
			if (queueItem) {
				this._snapshotUploadQueue.value.unshift(queueItem);
			}
		} finally {
			this._snapshotUploading = false;
		}
	}

	private async updateSnapshotStatus(
		snapshotId: string,
		status: "snapshot-taken" | "snapshot-uploaded" | "snapshot-failed",
		payload: CommandPayload
	) {
		const requestQueueItem = this._snapshotRequestQueue.value.find((r) => r.snapshotId == snapshotId);

		if (!requestQueueItem) {
			console.log("No original request found for snapshot", snapshotId);
			return;
		}

		requestQueueItem.status = status;

		if (status == "snapshot-uploaded") {
			const snapshot = payload as SnapshotUploaded;
			requestQueueItem.token = snapshot.token;
			requestQueueItem.status = "snapshot-uploaded";
			console.log(`Snapshot: Status=Uploaded, Id=${snapshotId}, ConversationId=${this.getRoomId()}, Token=${snapshot.token}`);
		}
	}

	private _isLaserPointerActive = ref(false);
	public get isLaserPointerActive() {
		return this._isLaserPointerActive.value;
	}
	public set isLaserPointerActive(value: boolean) {
		this._isLaserPointerActive.value = value;
	}

	private _laserPointerTargetUserId = ref<string>("");

	public get laserPointerTargetUserId(): string {
		return this._laserPointerTargetUserId.value;
	}

	public set laserPointerTargetUserId(id: string) {
		this._laserPointerTargetUserId.value = id;
	}

	private _laserPointerSenderId = ref<string>("");
	public get laserPointerSenderId(): string {
		return this._laserPointerSenderId.value;
	}
	public set laserPointerSenderId(id: string) {
		this._laserPointerSenderId.value = id;
	}

	public activateLaserPointer(targetUserId?: string): boolean {
		try {
			const activeAgentId = this.laserPointerSenderId;
			if (activeAgentId) {
				console.warn(`Cannot activate laser pointer, another agent ${activeAgentId} is using it.`);
				return false;
			}

			const currentUserId = this.getUserId();
			this.laserPointerTargetUserId = targetUserId || this.getFirstActiveNonHostOrManagerParticipantId();

			if (!this.laserPointerTargetUserId) {
				console.warn("Cannot activate laser pointer: no target user available");
				return false;
			}

			this.laserPointerSenderId = currentUserId;
			this.isLaserPointerActive = true;

			this.notifyLaserPointerStateToAllParticipants();

			return true;
		} catch (error) {
			console.error("Error activating laser pointer", error);
			return false;
		}
	}

	/**
	 * Check if the current user is the laser pointer owner (sender)
	 */
	public isLaserPointerOwner(): boolean {
		return this.isLaserPointerActive && this.laserPointerSenderId === this.getUserId();
	}

	/**
	 * Check if the current user is the laser pointer target
	 */
	public isLaserPointerTarget(): boolean {
		return this.isLaserPointerActive && this.laserPointerTargetUserId === this.getUserId();
	}

	/**
	 * Check if the current user can deactivate the laser pointer (either owner or target)
	 */
	public canDeactivateLaserPointer(): boolean {
		return (
			this.isLaserPointerActive &&
			(this.laserPointerSenderId === this.getUserId() || this.laserPointerTargetUserId === this.getUserId())
		);
	}

	public deactivateLaserPointer(): void {
		try {
			this.isLaserPointerActive = false;
			this.laserPointerTargetUserId = "";
			this.laserPointerSenderId = "";
			this.notifyLaserPointerStateToAllParticipants();
		} catch (e) {
			console.error("Error deactivating laser pointer", e);
		}
	}

	public sendLaserPointerStart(x: number, y: number): void {
		// Check if target user ID is set before sending
		if (!this._laserPointerTargetUserId.value) {
			console.warn("Cannot send laser pointer start: target user ID not set");
			return;
		}

		// Convert to integers for bandwidth optimization
		// Sends 1000 instead of 0.9998234734 (much smaller payload)
		const optimizedPayload = {
			c: "start",
			t: this._laserPointerTargetUserId.value,
			x: Math.round(x * 1000),
			y: Math.round(y * 1000)
		};
		this.sendCommand(optimizedPayload as any, this._laserPointerTargetUserId.value).catch((e) => {
			console.log("Error sending laser pointer start", e);
		});
	}

	public sendLaserPointerMove(x: number, y: number): void {
		// Check if target user ID is set before sending
		if (!this._laserPointerTargetUserId.value) {
			console.warn("Cannot send laser pointer move: target user ID not set");
			return;
		}

		// Convert to integers for bandwidth optimization
		// sends 1000 instead of 0.9998234734 (much smaller payload)
		const optimizedPayload = {
			c: "move",
			t: this._laserPointerTargetUserId.value,
			x: Math.round(x * 1000),
			y: Math.round(y * 1000)
		};
		this.sendCommand(optimizedPayload as any, this._laserPointerTargetUserId.value).catch((e) => {
			console.log("Error sending laser pointer move", e);
		});
	}

	public sendLaserPointerEnd(): void {
		// Check if target user ID is set before sending
		if (!this._laserPointerTargetUserId.value) {
			console.warn("Cannot send laser pointer end: target user ID not set");
			return;
		}

		const optimizedPayload = {
			c: "end",
			t: this._laserPointerTargetUserId.value
		};
		this.sendCommand(optimizedPayload as any, this._laserPointerTargetUserId.value).catch((e) => {
			console.log("Error sending laser pointer end", e);
		});
	}

	private notifyLaserPointerStateToAllParticipants(): void {
		const currentUserId = this.getUserId();
		const isActive = this.isLaserPointerActive;
		const payload: LaserPointerStateNotificationPayload = {
			id: uuidv4(),
			commandType: CommandType.LaserPointerStateNotification,
			senderId: currentUserId,
			timestamp: Date.now(),
			isActive,
			drawingUserId: isActive ? currentUserId : "",
			targetUserId: isActive ? this._laserPointerTargetUserId.value : ""
		};

		this.sendCommand(payload, undefined).catch((e) => {
			console.log("Error notifying agents about laser pointer state", e);
		});
	}

	private handleLaserPointerStateNotification(payload: LaserPointerStateNotificationPayload): void {
		const currentUserId = this.getUserId();
		const senderId = payload.senderId;

		// Ignore our own notifications
		if (senderId === currentUserId) {
			return;
		}

		if (payload.isActive) {
			const incomingDrawingUserId = payload.drawingUserId;

			// Conflict resolution: If we already have a sender, use drawing user ID as tie-breaker
			if (this.isLaserPointerActive && this.laserPointerSenderId && this.laserPointerSenderId !== incomingDrawingUserId) {
				const currentSenderId = this.laserPointerSenderId;
				const shouldKeepCurrent = currentSenderId < incomingDrawingUserId;

				if (shouldKeepCurrent) {
					// Current drawing user wins, ignore incoming notification
					return;
				}
				// Incoming drawing user wins, accept their notification
			}

			this.laserPointerSenderId = incomingDrawingUserId;
			this.laserPointerTargetUserId = payload.targetUserId;
			this.isLaserPointerActive = true;
		} else {
			// Only accept deactivation if it's from the current owner OR target
			if (this.laserPointerSenderId === payload.senderId || this.laserPointerTargetUserId === payload.senderId) {
				this.laserPointerSenderId = "";
				this.laserPointerTargetUserId = "";
				this.isLaserPointerActive = false;
			} else {
				console.warn("handleLaserPointerStateNotification: Ignoring deactivation from non-owner/target", {
					senderId: payload.senderId,
					currentOwner: this.laserPointerSenderId,
					currentTarget: this.laserPointerTargetUserId
				});
			}
		}
	}
}

export default Meeting;
export { Meeting };
